import { useId } from "react";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDialogBehaviour } from "./useDialogBehaviour";

export function FormModal({
  open,
  title,
  message,
  submitLabel,
  tone = "primary",
  busy = false,
  disabled = false,
  error,
  onSubmit,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  message?: string;
  submitLabel: string;
  tone?: "primary" | "danger";
  busy?: boolean;
  disabled?: boolean;
  error?: string;
  onSubmit: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const panel = useDialogBehaviour({ open, onDismiss: onCancel, dismissible: !busy });

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 items-center justify-center bg-app-ink/40 p-4 backdrop-blur-md"
      onClick={busy ? undefined : onCancel}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={message ? `${id}-message` : undefined}
        className="max-h-full w-full max-w-sm overflow-y-auto rounded-xl bg-surface p-6 shadow-card"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy && !disabled) onSubmit();
          }}
        >
          <div className="space-y-1">
            <h2 id={`${id}-title`} className="text-center text-base font-semibold text-app-ink">
              {title}
            </h2>
            {message && (
              <p id={`${id}-message`} className="text-sm leading-6 text-app-copy">
                {message}
              </p>
            )}
          </div>
          {children}
          {error && (
            <p className="text-sm text-accent-strong" role="alert">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={busy || disabled}
              className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-content-on-accent disabled:opacity-60 ${tone === "danger" ? "bg-accent-strong" : "bg-accent"}`}
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {submitLabel}
            </button>
            <button
              type="button"
              className="h-12 w-full rounded-xl bg-app-field text-base font-semibold text-app-ink disabled:opacity-60"
              onClick={onCancel}
              disabled={busy}
            >
              {t("cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
